/**
 * Client Coaching Page
 *
 * Page for clients to view their coaching engagements, upcoming sessions, and manage their coach relationships.
 * Route: /coaching/my-coach
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetEngagementsAsClientQuery,
  useGetUpcomingBookingsQuery,
  useGetBookingsQuery,
  useToggleAutoShareMutation,
  useRequestBookingMutation,
  useCancelBookingMutation,
  useCancelEngagementMutation,
  type CoachingEngagement,
  type CoachingBooking,
} from '../store/api/humanCoachingApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import toast from 'react-hot-toast';
import { mutationOutcome } from '../utils/mutationOutcome';

/** Coaches are listed on Marketplace → Experts (there is no /marketplace/coaches). */
export const FIND_COACH_PATH = '/marketplace/experts';

/** A booking's practice session opens in the owner's session review. */
export const bookingSessionReviewPath = (practiceSessionId: string) =>
  `/practice/sessions/${practiceSessionId}/review`;

type TabId = 'overview' | 'sessions' | 'history';

export function ClientCoachingPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Fetch data
  const {
    data: engagementsData,
    isLoading: loadingEngagements,
    isError: engagementsFailed,
    error: engagementsError,
    refetch: refetchEngagements,
  } = useGetEngagementsAsClientQuery({
    status: ['active'],
  });
  const { data: upcomingData, isLoading: loadingUpcoming } = useGetUpcomingBookingsQuery({
    asCoach: false,
    limit: 5,
  });
  const { data: allBookingsData, isLoading: loadingBookings } = useGetBookingsQuery({
    asCoach: false,
    limit: 50,
  });

  const isLoading = loadingEngagements || loadingUpcoming || loadingBookings;

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'overview', label: 'My Coaching' },
    { id: 'sessions', label: 'Upcoming Sessions', count: upcomingData?.bookings?.length },
    { id: 'history', label: 'History' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  // A failed load is not "you have no coach": say so and offer a retry.
  if (engagementsFailed) {
    return (
      <div className="max-w-2xl mx-auto py-16">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">My Coaching</h1>
        <LoadErrorNotice
          error={engagementsError}
          onRetry={() => void refetchEngagements()}
          failedMessage="Your coaching engagements could not be loaded."
          testIdPrefix="client-coaching"
        />
      </div>
    );
  }

  const activeEngagements = engagementsData?.engagements || [];

  if (activeEngagements.length === 0) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Work with a Coach</h1>
        <p className="text-gray-600 mb-8">
          You don't have any active coaching engagements yet. Find a coach to help you improve your
          communication skills.
        </p>
        <Link
          to={FIND_COACH_PATH}
          className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700"
        >
          Find a Coach
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Coaching</h1>
          <p className="mt-1 text-gray-500">
            Manage your coaching engagements and upcoming sessions.
          </p>
        </div>
        <Link
          to={FIND_COACH_PATH}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
        >
          Find More Coaches
        </Link>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && (
          <OverviewTab
            engagements={activeEngagements}
            upcomingBookings={upcomingData?.bookings || []}
          />
        )}
        {activeTab === 'sessions' && <SessionsTab bookings={upcomingData?.bookings || []} />}
        {activeTab === 'history' && (
          <HistoryTab
            bookings={(allBookingsData?.bookings || []).filter(
              (b) => b.status === 'completed' || b.status === 'cancelled'
            )}
          />
        )}
      </div>
    </div>
  );
}

// ============================================
// Tab Components
// ============================================

function OverviewTab({
  engagements,
  upcomingBookings,
}: {
  engagements: CoachingEngagement[];
  upcomingBookings: CoachingBooking[];
}) {
  return (
    <div className="space-y-8">
      {/* Active Engagements */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Coaches</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {engagements.map((engagement) => (
            <EngagementCard key={engagement.id} engagement={engagement} />
          ))}
        </div>
      </div>

      {/* Upcoming Sessions */}
      {upcomingBookings.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Sessions</h2>
          <div className="space-y-3">
            {upcomingBookings.map((booking) => (
              <UpcomingBookingCard key={booking.id} booking={booking} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const REQUEST_SESSION_FAILED = 'Your session request could not be sent. Please try again.';

/**
 * Send a session request. The form closes only once the server has accepted
 * it; on failure it stays open (with what the user typed) and shows why.
 */
export async function submitSessionRequest(
  send: () => { unwrap: () => Promise<unknown> },
  ui: { close: () => void; setError: (message: string | null) => void },
): Promise<boolean> {
  ui.setError(null);
  const outcome = await mutationOutcome(send(), REQUEST_SESSION_FAILED, ui.setError);
  if (outcome.ok) ui.close();
  return outcome.ok;
}

function EngagementCard({ engagement }: { engagement: CoachingEngagement }) {
  const [toggleAutoShare, { isLoading: isToggling }] = useToggleAutoShareMutation();
  const [cancelEngagement] = useCancelEngagementMutation();
  const [requestBooking, { isLoading: isRequesting }] = useRequestBookingMutation();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const handleToggleAutoShare = async () => {
    await mutationOutcome(
      toggleAutoShare({
        id: engagement.id,
        autoShare: !engagement.autoShareSessions,
      }),
      'Auto-share could not be changed. Please try again.',
      toast.error,
    );
  };

  const handleCancel = async () => {
    if (confirm('Are you sure you want to end this coaching engagement?')) {
      await mutationOutcome(
        cancelEngagement(engagement.id),
        'The coaching engagement could not be ended. Please try again.',
        toast.error,
      );
    }
  };

  const handleRequestSession = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    await submitSessionRequest(
      () =>
        requestBooking({
          engagementId: engagement.id,
          requestedTime: formData.get('requestedTime') as string || undefined,
          notes: formData.get('notes') as string || undefined,
        }),
      { close: () => setShowRequestForm(false), setError: setRequestError },
    );
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
            <span className="text-lg font-medium text-indigo-600">
              {engagement.coach?.firstName?.[0] || 'C'}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {engagement.coach?.firstName} {engagement.coach?.lastName}
            </h3>
            <p className="text-sm text-gray-500">{engagement.planLabel}</p>
          </div>
        </div>
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
            engagement.type === 'subscription'
              ? 'bg-purple-100 text-purple-800'
              : engagement.type === 'package'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-600'
          }`}
        >
          {engagement.type.replace('_', ' ')}
        </span>
      </div>

      <div className="mt-4 space-y-2 text-sm text-gray-500">
        <p>{engagement.sessionLengthMinutes} minute sessions</p>
        {engagement.maxSessionsPerMonth && (
          <p>{engagement.maxSessionsPerMonth} sessions per month</p>
        )}
      </div>

      {/* Auto-share toggle */}
      <div className="mt-4 flex items-center justify-between py-3 border-t">
        <div>
          <span className="text-sm font-medium text-gray-700">Auto-share sessions</span>
          <p className="text-xs text-gray-500">
            Automatically share your practice sessions with your coach
          </p>
        </div>
        <button
          onClick={handleToggleAutoShare}
          disabled={isToggling}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
            engagement.autoShareSessions ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              engagement.autoShareSessions ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => setShowRequestForm(!showRequestForm)}
          className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
        >
          Request Session
        </button>
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50"
        >
          End
        </button>
      </div>

      {/* Request Form */}
      {showRequestForm && (
        <form onSubmit={handleRequestSession} className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Preferred Time (optional)
            </label>
            <input
              type="datetime-local"
              name="requestedTime"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
            <textarea
              name="notes"
              rows={2}
              placeholder="What would you like to work on?"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            />
          </div>
          {requestError && (
            <p className="text-sm text-red-600" role="alert" data-testid="session-request-error">
              {requestError}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isRequesting}
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              Send Request
            </button>
            <button
              type="button"
              onClick={() => {
                setShowRequestForm(false);
                setRequestError(null);
              }}
              className="px-4 py-2 border border-gray-300 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function UpcomingBookingCard({ booking }: { booking: CoachingBooking }) {
  const [cancelBooking] = useCancelBookingMutation();

  const handleCancel = async () => {
    if (confirm('Are you sure you want to cancel this session?')) {
      await mutationOutcome(
        cancelBooking(booking.id),
        'The session could not be cancelled. Please try again.',
        toast.error,
      );
    }
  };

  return (
    <div className="bg-white rounded-lg border p-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
          <span className="text-sm font-medium text-indigo-600">
            {booking.coach?.firstName?.[0] || 'C'}
          </span>
        </div>
        <div>
          <h4 className="font-medium text-gray-900">
            Session with {booking.coach?.firstName} {booking.coach?.lastName}
          </h4>
          <p className="text-sm text-gray-500">
            {booking.scheduledTime
              ? new Date(booking.scheduledTime).toLocaleString()
              : booking.requestedTime
                ? `Requested: ${new Date(booking.requestedTime).toLocaleString()}`
                : 'Time TBD'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
            booking.status === 'scheduled'
              ? 'bg-green-100 text-green-800'
              : 'bg-orange-100 text-orange-800'
          }`}
        >
          {booking.status}
        </span>
        <button
          onClick={handleCancel}
          className="text-sm text-gray-500 hover:text-red-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function SessionsTab({ bookings }: { bookings: CoachingBooking[] }) {
  if (bookings.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No upcoming sessions scheduled.</p>
        <p className="mt-2 text-sm">Request a session with your coach to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bookings.map((booking) => (
        <UpcomingBookingCard key={booking.id} booking={booking} />
      ))}
    </div>
  );
}

function HistoryTab({ bookings }: { bookings: CoachingBooking[] }) {
  if (bookings.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No session history yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {bookings.map((booking) => (
        <div key={booking.id} className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">
                Session with {booking.coach?.firstName} {booking.coach?.lastName}
              </h4>
              <p className="text-sm text-gray-500">
                {booking.completedAt
                  ? new Date(booking.completedAt).toLocaleString()
                  : booking.cancelledAt
                    ? new Date(booking.cancelledAt).toLocaleString()
                    : 'N/A'}
              </p>
            </div>
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                booking.status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {booking.status}
            </span>
          </div>
          {booking.practiceSessionId && (
            <div className="mt-2">
              <Link
                to={bookingSessionReviewPath(booking.practiceSessionId)}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                View Practice Session
              </Link>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default ClientCoachingPage;
